const TIER_ASSETS = "/iceholidays-assets/images/tier";

export interface TierPerk {
  icon: string;
  text: string;
  inset?: [number, number];
}

export interface TierDetail {
  // Matches tierMeta's TierOption.value, i.e. the tour's `brand`.
  value: string;
  tab: string;
  heading: string;
  // Mobile: the heptagon badge behind the crown.
  badge: string;
  crownBase: string;
  crownTop: string;
  // Desktop: the heptagon + soft glow that sit behind the crown in the top-right of the tier's panel.
  panelShape: string;
  panelGlow: string;
  perks: TierPerk[];
}

// Figma insets 7.14% / 10.71% (shopping bag) and 1.56% (hotel building) of the icon slot.
const SHOPPING_BAG_INSET: [number, number] = [0.0714, 0.1071];
const HOTEL_INSET: [number, number] = [0.0156, 0.0156];

const perk = (icon: string, text: string, inset?: [number, number]): TierPerk => ({
  icon: `${TIER_ASSETS}/${icon}.svg`,
  text,
  inset,
});

export const TIER_DETAILS: TierDetail[] = [
  {
    value: "gt_3a",
    tab: "3A",
    heading: "GT 3A: Essential Comfort",
    badge: `${TIER_ASSETS}/tier-3a-badge.svg`,
    crownBase: `${TIER_ASSETS}/tier-3a-crown-base.png`,
    crownTop: `${TIER_ASSETS}/tier-3a-crown-top.png`,
    panelShape: `${TIER_ASSETS}/tier-panel-shape-3a-4a.svg`,
    panelGlow: `${TIER_ASSETS}/tier-panel-glow-3a.svg`,
    perks: [
      perk("perk-stay", "Accommodation: Guaranteed stay at budget-friendly 3-star hotels (or higher) as rated by GD standards."),
      perk("perk-transport", "Transport: Reliable tour vehicles with a dedicated seat provided for every traveller."),
      perk("perk-costs", 'No Hidden Costs: The only optional activities requiring additional fees are those explicitly listed under "Exclusions."'),
      perk("perk-shopping", "Shopping Regulation: Transparent and fair; shopping stops will not exceed the total number of tour days", SHOPPING_BAG_INSET),
    ],
  },
  {
    value: "gt_4a",
    tab: "4A",
    heading: "GT 4A: Upscale Experience",
    badge: `${TIER_ASSETS}/tier-4a-badge.svg`,
    crownBase: `${TIER_ASSETS}/tier-4a-crown-base.png`,
    crownTop: `${TIER_ASSETS}/tier-4a-crown-top.png`,
    panelShape: `${TIER_ASSETS}/tier-panel-shape-3a-4a.svg`,
    panelGlow: `${TIER_ASSETS}/tier-panel-glow-4a.svg`,
    perks: [
      perk("perk-stay", "Accommodation: Stay in 4-star upscale hotels (or higher). These premium properties comprise at least 70% of the total nights in the itinerary."),
      perk("perk-transport", "Transport: Enhanced comfort with a vehicle occupancy cap—ensuring at least 10% empty seats for a spacious journey (excluding 2+1 Business Coaches)."),
      perk("perk-costs", 'No Hidden Costs: Only pre-disclosed "Exclusions" will be recommended as optional activities.'),
      perk("perk-shopping", "Shopping Regulation: Minimal intrusion; an average of no more than one shopping stop every two days.", SHOPPING_BAG_INSET),
    ],
  },
  {
    value: "gt_5a",
    tab: "5A",
    heading: "GT 5A: Luxury Leisure",
    badge: `${TIER_ASSETS}/tier-5a-badge.svg`,
    crownBase: `${TIER_ASSETS}/tier-5a-crown-base.png`,
    crownTop: `${TIER_ASSETS}/tier-5a-crown-top.png`,
    panelShape: `${TIER_ASSETS}/tier-panel-shape-5a.svg`,
    panelGlow: `${TIER_ASSETS}/tier-panel-glow-5a.svg`,
    perks: [
      perk("perk-stay", "Accommodation: Indulge in 5-star luxury hotels (or higher) for at least 70% of the trip. We provide a maximum of three clearly defined reference hotels per day for absolute transparency."),
      perk("perk-transport", "Transport: Maximum comfort with a vehicle occupancy cap—ensuring at least 15% empty seats for a relaxed experience (excluding 2+1 Business Coaches)."),
      perk("perk-costs", 'No Hidden Costs: Strictly no "surprise" fees; only activities in the "Exclusions" list will be suggested.'),
      perk("perk-shopping", "Shopping Regulation: Zero-hassle focus. A curated itinerary designed for peace of mind and pure travel enjoyment.", SHOPPING_BAG_INSET),
    ],
  },
  {
    value: "gt_5a_plus",
    tab: "5A+",
    heading: "GD 5A+: The Ultimate Collection",
    badge: `${TIER_ASSETS}/tier-5a-plus-badge.svg`,
    crownBase: `${TIER_ASSETS}/tier-5a-plus-crown-base.png`,
    crownTop: `${TIER_ASSETS}/tier-5a-plus-crown-top.png`,
    panelShape: `${TIER_ASSETS}/tier-panel-shape-5a-plus.svg`,
    panelGlow: `${TIER_ASSETS}/tier-panel-glow-5a-plus.svg`,
    perks: [
      perk("perk-luxury", "Hidden Luxury: Exclusive stays at Gold, Platinum, and Diamond rated properties or world-renowned luxury brands.", HOTEL_INSET),
      perk("perk-vip", "VIP Perks: Priority access through VIP channels, expert/specialist guides, and reserved premium seating at all venues."),
      perk("perk-fleet", "Premium Fleet: Private transfers via Executive/Business class vehicles."),
      perk("perk-service", "Elite Service: Dedicated mid-to-senior level tour guides throughout the journey, backed by 24-hour exclusive concierge support."),
    ],
  },
];
